import { getDateWithoutTimezoneShift } from '@lender/utils';
import {
  LOAN_EDITOR_ACTION_SECTION_NAME,
  LoanApplicationTabIdToName,
} from 'app/pages/loanApplication/LoanApplication.constants';
import { getImpersonatedByUserLabel } from 'app/pages/loanApplication/LoanApplication.utils';
import dayjs from 'dayjs';
import _ from 'lodash';
import { nanoid } from 'nanoid';

import { COMMON_DATETIME_FORMAT } from 'constants/date';
import { HistoryFallback } from 'constants/history';
import { LoanChangesHistoryEntityType, LoanChangesHistoryFieldType } from 'constants/loanChanges';
import { getConditionalArray } from 'utils/data';
import { formatDateStringWithDefaultOffset } from 'utils/dayjs';
import { getPersonFullName } from 'utils/loanApplication';
import { checkIsFieldDisplayed } from 'utils/loanChanges';

import {
  buildCustomFieldHistoryLabel,
  formatCustomFieldValue,
  isCustomFieldHistoryNoiseProperty,
  isCustomFieldHistoryProperty,
  parseCustomFieldEntityPrimaryKey,
  resolveCustomFieldNavigation,
  resolveCustomFieldTabName,
} from './customFieldHistoryRow.utils';
import { loanChangesHistoryFieldsProxy } from './LoanChangesHistoryFields.proxy';
import { FEE_TYPE_ID_PROPERTY } from './LoanChangesHistoryTable.constants';

const FORMATTED_MICRO_SECONDS = 6;
const TIME_STAMP_IN_MICRO_SECONDS = 1000;

const getTimeStampValue = dateString => {
  if (!dayjs(dateString).isValid()) {
    return dateString;
  }

  const microSeconds = dateString.match(/(?:\.)[0-9]{1,6}/g)?.[0]?.slice(1) || 0;
  const formattedMicroSeconds = _.padEnd(microSeconds, FORMATTED_MICRO_SECONDS, '0');
  const timeStampInMicroSeconds =
    dayjs(dateString).startOf('second').valueOf() * TIME_STAMP_IN_MICRO_SECONDS;

  return Number(timeStampInMicroSeconds) + Number(formattedMicroSeconds);
};

const checkIsFeeHistoryRow = ({ rootEntityTypeName }) =>
  rootEntityTypeName === LoanChangesHistoryEntityType.LOAN_FEE_HISTORY;

// Every column of a fee-history row shares the row's primary key; the API resolves its fee type id to the fee's name.
const buildFeeNameByEntityPrimaryKey = data =>
  Object.fromEntries(
    (data ?? [])
      .filter(row => checkIsFeeHistoryRow(row) && row.propertyName === FEE_TYPE_ID_PROPERTY)
      .map(({ entityPrimaryKey, newValueDetails, oldValueDetails }) => [
        entityPrimaryKey,
        newValueDetails ?? oldValueDetails,
      ])
      .filter(([, feeName]) => !_.isNil(feeName)),
  );

const getFeeHistoryName = (props, feeNameByEntityPrimaryKey) =>
  checkIsFeeHistoryRow(props) ? (feeNameByEntityPrimaryKey[props.entityPrimaryKey] ?? null) : null;

const checkIsChangeValuable = ({ newValue, oldValue }) => {
  if (newValue === oldValue) {
    return false;
  }

  if (_.isNull(oldValue) && newValue === false) {
    return false;
  }

  if (_.isNull(newValue) && oldValue === false) {
    return false;
  }

  return true;
};

const getFormattedSelectValues = ({ oldValue, newValue, config }) => {
  const options = config.inputProps?.options;

  if (!options?.length) {
    return [oldValue, newValue];
  }

  const oldValueOption = options.find(option => option.value === oldValue);
  const newValueOption = options.find(option => option.value === newValue);

  return [oldValueOption?.name || oldValue, newValueOption?.name || newValue];
};

const getFormattedValues = (
  { oldValue, newValue, type, oldValueDetails, newValueDetails },
  config,
) => {
  const initiallyFormattedValues = [oldValueDetails, newValueDetails];
  const isValuesInitiallyFormatted = !initiallyFormattedValues.every(_.isNil);

  if (isValuesInitiallyFormatted) {
    return initiallyFormattedValues;
  }

  const values = [oldValue, newValue];

  if (type === LoanChangesHistoryFieldType.DATE_TIME) {
    return values.map(value => {
      if (!value) {
        return value;
      }

      const format = config.inputProps?.format || COMMON_DATETIME_FORMAT;
      const dateStringWithOffset = formatDateStringWithDefaultOffset(value);

      const datePattern = /T00:00:00.0000000/;
      const isDateZeroTimeFormat = datePattern.test(dateStringWithOffset);

      const valueToFormat = isDateZeroTimeFormat
        ? getDateWithoutTimezoneShift(dateStringWithOffset)
        : dateStringWithOffset;

      return dayjs(valueToFormat).format(format);
    });
  }

  if (config.inputProps?.customFormat) {
    try {
      return values.map(config.inputProps.customFormat);
    } catch {
      return values;
    }
  }

  if (config.type === LoanChangesHistoryFieldType.SELECT) {
    return getFormattedSelectValues({ oldValue, newValue, config });
  }

  return values;
};

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// A LoanFee entityPrimaryKey is "{FeeTypeId}_{LoanId}_{IsOverride}".
const resolveFeeName = ({ entityPrimaryKey }, feeTypeContext) => {
  const [feeTypeId] = _.isString(entityPrimaryKey) ? entityPrimaryKey.split('_') : [];

  return GUID_PATTERN.test(feeTypeId)
    ? (feeTypeContext?.feeTypeNamesById?.[feeTypeId] ?? null)
    : null;
};

const getFormattedFieldName = (
  { propertyName, rootEntityTypeName, parentProperty },
  config,
  { ownerName = null, feeName = null } = {},
) => {
  // Need this part to meet BE restriction about Adjustment pending changes
  const isLoanAdjustmentEntityTypeName =
    rootEntityTypeName === LoanChangesHistoryEntityType.LOAN_ADJUSTMENT;

  const defaultFieldName = _.startCase(_.upperFirst(propertyName));
  const fieldName = config.inputProps?.label || defaultFieldName;

  const parentProperties =
    parentProperty?.split('.').map(propertyName => `[${propertyName}]`) ?? [];

  return [
    feeName ?? `[${rootEntityTypeName}]`,
    ...getConditionalArray(!_.isNil(ownerName), () => [`[${ownerName}]`]),
    ...parentProperties,
    isLoanAdjustmentEntityTypeName ? 'Adjustment' : fieldName,
  ]
    .filter(Boolean)
    .join(' ');
};

const formatToCommonDateTime = date => dayjs(date).format(COMMON_DATETIME_FORMAT);

// A saved change's history time is when it was saved (appliedDate), so one save reads as one moment in
// the log; an unsaved/pending change and legacy rows written before applied dates existed fall back to
// when the change was made.
const resolveHistoryDate = ({ appliedDate, createdDate }) => appliedDate ?? createdDate;

const buildCustomFieldRow = ({
  props,
  createdByUser,
  createdByOriginalUser,
  createdDate,
  customFieldContext,
}) => {
  if (isCustomFieldHistoryNoiseProperty(props.propertyName)) {
    return null;
  }

  if (!isCustomFieldHistoryProperty(props.propertyName)) {
    return null;
  }

  const parsedKey = parseCustomFieldEntityPrimaryKey(props.entityPrimaryKey);
  const definitionId = parsedKey?.definitionId ?? null;
  const targetId = parsedKey?.targetId ?? null;

  const definition = definitionId
    ? (customFieldContext?.customFieldDefinitionsById?.[definitionId] ?? null)
    : null;

  const formattedOldValue = formatCustomFieldValue(props.oldValue, definition?.valueType);
  const formattedNewValue = formatCustomFieldValue(props.newValue, definition?.valueType);

  if (!checkIsChangeValuable({ newValue: formattedNewValue, oldValue: formattedOldValue })) {
    return null;
  }

  const subjectPropertyId = customFieldContext?.subjectPropertyId ?? null;

  const historyDate = resolveHistoryDate({ appliedDate: props.appliedDate, createdDate });

  const navigation = resolveCustomFieldNavigation({
    target: definition?.target,
    targetId,
    subjectPropertyId,
  });

  const createdByUserUserFullName = getPersonFullName(createdByUser || {});
  const createdByOriginalUserFullName = getPersonFullName(createdByOriginalUser || {});

  return {
    ...props,
    oldValue: formattedOldValue,
    newValue: formattedNewValue,
    tabName:
      resolveCustomFieldTabName({
        target: definition?.target,
        targetId,
        subjectPropertyId,
      }) ??
      LoanApplicationTabIdToName[props.tabId] ??
      null,
    navigation,
    propertyName: buildCustomFieldHistoryLabel({ definition }),
    fullName: createdByUserUserFullName,
    formattedFullName: getImpersonatedByUserLabel({
      userProp: createdByUserUserFullName,
      impersonatedUserProp: createdByOriginalUserFullName,
      fallbackUserProp: HistoryFallback.SYSTEM,
    }),
    userName: createdByUser?.email,
    formattedUserName: getImpersonatedByUserLabel({
      userProp: createdByUser?.email,
      impersonatedUserProp: createdByOriginalUser?.email,
      fallbackUserProp: HistoryFallback.SYSTEM_EMAIL,
    }),
    modifiedOn: formatToCommonDateTime(historyDate),
    modifiedOnRaw: getTimeStampValue(historyDate),
    id: nanoid(),
  };
};

export const normalizeLoanChangesHistoryTableData = (
  data,
  customFieldContext = null,
  feeTypeContext = null,
) => {
  const feeNameByEntityPrimaryKey = buildFeeNameByEntityPrimaryKey(data);

  return data?.reduce((acc, { createdByUser, createdByOriginalUser, createdDate, ...props }) => {
    if (props.rootEntityTypeName === LoanChangesHistoryEntityType.CUSTOM_FIELD) {
      const customFieldRow = buildCustomFieldRow({
        props,
        createdByUser,
        createdByOriginalUser,
        createdDate,
        customFieldContext,
      });

      if (customFieldRow) {
        acc.push(customFieldRow);
      }

      return acc;
    }

    const isFieldDisplayed = checkIsFieldDisplayed(props);

    if (isFieldDisplayed) {
      const configsByEntityType = loanChangesHistoryFieldsProxy(props.rootEntityTypeName);
      const configByPropertyName = configsByEntityType?.[props.propertyName] || {};
      const [oldValue, newValue] = getFormattedValues(props, configByPropertyName);

      const isChangeValuable = checkIsChangeValuable({ newValue, oldValue });

      if (!isChangeValuable) {
        return acc;
      }

      const createdByUserUserFullName = getPersonFullName(createdByUser || {});
      const createdByOriginalUserFullName = getPersonFullName(createdByOriginalUser || {});

      const historyDate = resolveHistoryDate({ appliedDate: props.appliedDate, createdDate });

      acc.push({
        ...props,
        oldValue,
        newValue,
        tabName: LoanApplicationTabIdToName[props.tabId] ?? null,
        propertyName: getFormattedFieldName(props, configByPropertyName, {
          ownerName: getFeeHistoryName(props, feeNameByEntityPrimaryKey),
          feeName:
            props.rootEntityTypeName === LoanChangesHistoryEntityType.LOAN_FEE
              ? resolveFeeName(props, feeTypeContext)
              : null,
        }),
        fullName: createdByUserUserFullName,
        formattedFullName: getImpersonatedByUserLabel({
          userProp: createdByUserUserFullName,
          impersonatedUserProp: createdByOriginalUserFullName,
          fallbackUserProp: HistoryFallback.SYSTEM,
        }),
        userName: createdByUser?.email,
        formattedUserName: getImpersonatedByUserLabel({
          userProp: createdByUser?.email,
          impersonatedUserProp: createdByOriginalUser?.email,
          fallbackUserProp: HistoryFallback.SYSTEM_EMAIL,
        }),
        modifiedOn: formatToCommonDateTime(historyDate),
        modifiedOnRaw: getTimeStampValue(historyDate),
        id: nanoid(),
      });
    }

    return acc;
  }, []);
};

export const normalizeLoanActionsHistoryTableData = data =>
  data?.map(({ createdByUser, createdByOriginalUser, createdDate, ...props }) => {
    const createdByUserUserFullName = getPersonFullName(createdByUser || {});
    const createdByOriginalUserFullName = getPersonFullName(createdByOriginalUser || {});

    return {
      ...props,
      oldValue: '',
      newValue: '',
      tabName: LOAN_EDITOR_ACTION_SECTION_NAME,
      propertyName: props.rootEntityTypeName?.toUpperCase(),
      fullName: createdByUserUserFullName,
      formattedFullName: getImpersonatedByUserLabel({
        userProp: createdByUserUserFullName,
        impersonatedUserProp: createdByOriginalUserFullName,
      }),
      userName: createdByUser?.email,
      formattedUserName: getImpersonatedByUserLabel({
        userProp: createdByUser?.email,
        impersonatedUserProp: createdByOriginalUser?.email,
      }),
      modifiedOn: formatToCommonDateTime(createdDate),
      modifiedOnRaw: getTimeStampValue(createdDate),
      id: nanoid(),
    };
  });
